import {animalSurface} from './animal-surface.js';

// Overview keeps its existing 92 × 68 budget. Close contact patches use the
// SAME Float32 heights and diagonal as support / WebGL. Farther terrain uses
// aligned, stitched levels from those vertices; tiles cull before projection.
const surfaces=new WeakMap(),frames=new WeakMap(),TILE=8;
export const CANVAS_FINE_SURFACE_RADIUS=.75;
export const CANVAS_CLOSE_SURFACE_DISTANCE=6;
const outcode=p=>(p.clipZ+p.clipW<0?1:0)|(p.clipW-p.clipZ<0?2:0)|(p.clipX+p.clipW<0?4:0)|(p.clipW-p.clipX<0?8:0)|(p.clipY+p.clipW<0?16:0)|(p.clipW-p.clipY<0?32:0);

export function canvasCloseSurfaceEnabled(world){
 const p=world.camera.position,t=world.cameraTarget;
 return !!t&&Math.hypot(p.x-t.x,p.y-t.y,p.z-t.z)<CANVAS_CLOSE_SURFACE_DISTANCE;
}

function geometry(terrain){
 let cached=surfaces.get(terrain);if(cached)return cached;
 const surface=animalSurface(terrain),{nx,nz,heights}=surface,points=[],tiles=[];
 for(let j=0;j<=nz;j++)for(let i=0;i<=nx;i++)points.push({x:-6+i*12/nx,y:heights[j*(nx+1)+i],z:-4.5+j*9/nz});
 for(let j=0;j<nz;j+=TILE)for(let i=0;i<nx;i+=TILE){
  const endI=Math.min(nx,i+TILE),endJ=Math.min(nz,j+TILE);let low=Infinity,high=-Infinity;
  for(let z=j;z<=endJ;z++)for(let x=i;x<=endI;x++){const y=heights[z*(nx+1)+x];low=Math.min(low,y);high=Math.max(high,y);}
  tiles.push({i,j,endI,endJ,minX:points[j*(nx+1)+i].x,maxX:points[j*(nx+1)+endI].x,minZ:points[j*(nx+1)+i].z,maxZ:points[endJ*(nx+1)+i].z,low,high});
 }
 cached={surface,points,tiles};surfaces.set(terrain,cached);return cached;
}

// Neighboring levels use the finer edge subdivision on BOTH sides. A coarse
// tile fans from an authoritative center through those shared seam vertices;
// no skirt or overlapping offset surface. Level 1 keeps the original diagonal.
function tileTriangles(i,j,endI,endJ,step,nx,edgeSteps){
 const index=(x,z)=>z*(nx+1)+x,result=[];
 for(let z=j;z<endJ;z+=step)for(let x=i;x<endI;x+=step){
  const ex=Math.min(endI,x+step),ez=Math.min(endJ,z+step);
  if(step===1){const a=index(x,z),b=index(ex,z),c=index(x,ez),d=index(ex,ez);result.push([a,c,b],[b,c,d]);continue;}
  const center=index((x+ex)/2,(z+ez)/2),ring=[];
  for(let zz=z;zz<ez;zz+=x===i?edgeSteps[0]:step)ring.push(index(x,zz));
  for(let xx=x;xx<ex;xx+=ez===endJ?edgeSteps[1]:step)ring.push(index(xx,ez));
  for(let zz=ez;zz>z;zz-=ex===endI?edgeSteps[2]:step)ring.push(index(ex,zz));
  for(let xx=ex;xx>x;xx-=z===j?edgeSteps[3]:step)ring.push(index(xx,z));
  for(let n=0;n<ring.length;n++)result.push([center,ring[n],ring[(n+1)%ring.length]]);
 }
 return result;
}

export function prepareCanvasCloseSurface(world,terrain){
 if(!canvasCloseSurfaceEnabled(world))return null;
 const key=[world.w,world.h,world.cameraTarget.x,world.cameraTarget.y,world.cameraTarget.z,...world.camera.matrixWorldInverse.elements,...world.camera.projectionMatrix.elements].join(','),old=frames.get(world);
 if(old?.terrain===terrain&&old.key===key)return old.frame;
 const {surface,points,tiles}=geometry(terrain),projected=new Array(points.length),triangles=[];
 let visibleTiles=0,projectedVertices=0;const levelTiles=[0,0,0],target=world.cameraTarget,distance=world.camera.position.distanceTo(target),fineRadius=Math.max(CANVAS_FINE_SURFACE_RADIUS,Math.min(3,distance*2));
 const vertex=k=>{if(!projected[k]){const v=points[k];projected[k]={...v,p:world.project(v.x,v.y,v.z)};projectedVertices++;}return projected[k];};
 const levels=tiles.map(tile=>{const distanceToTile=Math.hypot(Math.max(tile.minX-target.x,0,target.x-tile.maxX),Math.max(tile.minZ-target.z,0,target.z-tile.maxZ));return distanceToTile<=fineRadius?0:distanceToTile<=fineRadius+.75?1:2;}),columns=Math.ceil(surface.nx/TILE);
 for(const [tileIndex,tile]of tiles.entries()){
  let code=63;
  for(const x of[tile.minX,tile.maxX])for(const y of[tile.low,tile.high])for(const z of[tile.minZ,tile.maxZ])code&=outcode(world.project(x,y,z));
  if(code)continue;visibleTiles++;
  const level=levels[tileIndex],step=[1,4,8][level];levelTiles[level]++;
  const neighbor=(index,exists)=>exists?[1,4,8][levels[index]]:step;
  const edgeSteps=[neighbor(tileIndex-1,tile.i>0),neighbor(tileIndex+columns,tile.endJ<surface.nz),neighbor(tileIndex+1,tile.endI<surface.nx),neighbor(tileIndex-columns,tile.j>0)].map(s=>Math.min(step,s));
  const variant=[step,...edgeSteps].join(',');let idsForTile=tile.variant===variant?tile.idsForTile:null;
  if(!idsForTile){idsForTile=tileTriangles(tile.i,tile.j,tile.endI,tile.endJ,step,surface.nx,edgeSteps);tile.variant=variant;tile.idsForTile=idsForTile;}
  for(const ids of idsForTile){
   const vs=ids.map(vertex);if(outcode(vs[0].p)&outcode(vs[1].p)&outcode(vs[2].p))continue;
   const polygon=world.clipPolygon(vs.map(v=>v.p));if(polygon.length<3)continue;
   triangles.push({vs,polygon,depth:vs.reduce((n,v)=>n+v.p.cameraDepth,0)/3});
  }
 }
 triangles.sort((a,b)=>b.depth-a.depth);
 const frame={surface,triangles,stats:{totalTiles:tiles.length,visibleTiles,projectedVertices,visibleTriangles:triangles.length,fineRadius,levelTiles}};
 frames.set(world,{terrain,key,frame});return frame;
}

/** Clip a rendered terrain triangle to the camera's side of the water plane.
 * A ray can meet this half-space only BEFORE its unique water-plane hit. Thus
 * its projected union is an exact lake occluder, without a painter-order guess.
 * It also works for a camera below the plane; no assumption about bank slope.
 */
export function clipTerrainToWaterSide(vertices,level,cameraY){
 const sign=cameraY>=level?1:-1,polygon=[];let a=vertices.at(-1),da=(a.y-level)*sign;
 for(const b of vertices){const db=(b.y-level)*sign;
  if((da>=0)!==(db>=0)){const t=da/(da-db);polygon.push({x:a.x+(b.x-a.x)*t,y:level,z:a.z+(b.z-a.z)*t});}
  if(db>=0)polygon.push(b);a=b;da=db;
 }
 return polygon;
}

function appendPath(ctx,polygon){
 if(polygon.length<3)return false;
 // Normalize winding so overlapping front/back terrain faces form a union.
 const area=polygon.reduce((sum,p,i)=>{const q=polygon[(i+1)%polygon.length];return sum+p.x*q.y-q.x*p.y;},0);
 if(area<0)polygon=[...polygon].reverse();ctx.moveTo(polygon[0].x,polygon[0].y);
 for(let i=1;i<polygon.length;i++)ctx.lineTo(polygon[i].x,polygon[i].y);ctx.closePath();return true;
}

/** The primary lake's actual 112-sector footprint; terrain/water intersections
 * are established by the same rendered triangles, never radial height search.
 */
export function canvasLakeFootprint(terrain){
 const l=terrain.layout.lake,ring=[];
 for(let j=0;j<=112;j++){const a=j/112*Math.PI*2,rad=terrain.lakeBoundary(a)*1.10;
  ring.push({x:Math.max(-5.995,Math.min(5.995,l.x+Math.cos(a)*l.rx*rad)),z:Math.max(-4.506,Math.min(4.506,l.z+Math.sin(a)*l.rz*rad))});
 }
 return ring;
}

export function drawCanvasCloseLake(world,ctx,terrain,frame){
 const level=.16+world.state.water*.06,frozen=world.state.season==='winter'&&world.state.snowCover>.1;
 let canvas=world.closeLakeCanvas;if(!canvas)canvas=world.closeLakeCanvas=document.createElement('canvas');
 const width=world.canvas.width,height=world.canvas.height;
 if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;}
 const lakeKey=[level,frozen,width,height,world.dpr].join(',');
 if(world.closeLakeFrame===frame&&world.closeLakeKey===lakeKey){ctx.drawImage(canvas,0,0,world.w,world.h);return;}
 world.closeLakeFrame=frame;world.closeLakeKey=lakeKey;
 const layer=canvas.getContext('2d');layer.setTransform(1,0,0,1,0,0);layer.clearRect(0,0,width,height);layer.setTransform(world.dpr,0,0,world.dpr,0,0);
 const l=terrain.layout.lake,ring=canvasLakeFootprint(terrain),center=world.project(l.x,level,l.z),polygons=[];
 for(let i=0;i<112;i++){
  const a=ring[i],b=ring[i+1],polygon=world.clipPolygon([center,world.project(a.x,level,a.z),world.project(b.x,level,b.z)]);
  if(polygon.length>=3)polygons.push(polygon);
 }
 if(!polygons.length){frame.stats.lakePolygons=0;frame.stats.lakeOccluders=0;return;}
 layer.save();layer.beginPath();for(const polygon of polygons)appendPath(layer,polygon);layer.clip();
 let cp=center,rim=world.project(l.x+l.rx,level,l.z),radius=Math.hypot(rim.x-cp.x,rim.y-cp.y);
 if(!cp.visible||!rim.visible){const points=polygons.flat();cp={x:points.reduce((n,p)=>n+p.x,0)/points.length,y:points.reduce((n,p)=>n+p.y,0)/points.length};radius=Math.max(1,...points.map(p=>Math.hypot(p.x-cp.x,p.y-cp.y)));}
 const gradient=layer.createRadialGradient(cp.x,cp.y,Math.max(1,radius)*.1,cp.x,cp.y,Math.max(1,radius));
 gradient.addColorStop(0,frozen?'#9db7ac':'#164557');gradient.addColorStop(.65,frozen?'#8ba89f':'#2d6b73');gradient.addColorStop(1,frozen?'#b4c7b4':'#749992');layer.fillStyle=gradient;layer.fillRect(0,0,world.w,world.h);
 // Keep the ordinary seeded ripples, on the current water plane (not fixed .183).
 let seed=89;const random=()=>{seed=(Math.imul(1664525,seed)+1013904223)>>>0;return seed/4294967296;};
 for(let i=0;i<260;i++){const x=.6+random()*4.4,z=.9+random()*3.3,p=world.project(x,level,z),len=.6+random()*5;if(!p.visible)continue;
  layer.strokeStyle=i%11===0?'#dfd3a655':`rgba(158,196,163,${.04+random()*.12})`;layer.lineWidth=.3+random()*.5;layer.beginPath();layer.moveTo(p.x-len,p.y);layer.quadraticCurveTo(p.x,p.y-.5,p.x+len,p.y+.4);layer.stroke();
 }
 layer.restore();layer.save();layer.globalCompositeOperation='destination-out';layer.fillStyle='#000';layer.beginPath();let occluders=0;
 const side=world.camera.position.y>=level?1:-1;
 for(const triangle of frame.triangles){const {vs}=triangle;
  if(vs.every(v=>(v.y-level)*side>=0)){if(appendPath(layer,triangle.polygon))occluders++;continue;}
  const bank=clipTerrainToWaterSide(vs,level,world.camera.position.y);if(bank.length<3)continue;
  const polygon=world.clipPolygon(bank.map(v=>world.project(v.x,v.y,v.z)));if(appendPath(layer,polygon))occluders++;
 }
 // Fill the union once: adjacent triangle edges do not create alpha seams.
 layer.fill();layer.restore();ctx.drawImage(canvas,0,0,world.w,world.h);
 frame.stats.lakePolygons=polygons.length;frame.stats.lakeOccluders=occluders;
}


/** One image and one geometry frame per world. Wind-only static redraws do not
 * re-project/shade 3D terrain. Lighting, surface state, viewport and camera all
 * participate in invalidation; ordinary overview retains its original path.
 */
export function drawCanvasCloseTerrain(world,ctx,frame){
 const s=world.state,width=world.canvas.width,height=world.canvas.height,key=[width,height,world.dpr,s.sun,s.storm,s.snow,s.snowCover,s.wetness??0,s.season,...(s.seasonMix??[])].join(',');
 let canvas=world.closeTerrainCanvas;if(!canvas)canvas=world.closeTerrainCanvas=document.createElement('canvas');
 if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;}
 if(world.closeTerrainFrame===frame&&world.closeTerrainKey===key){ctx.drawImage(canvas,0,0,world.w,world.h);return;}
 world.closeTerrainFrame=frame;world.closeTerrainKey=key;
 const layer=canvas.getContext('2d');layer.setTransform(1,0,0,1,0,0);layer.clearRect(0,0,width,height);layer.setTransform(world.dpr,0,0,world.dpr,0,0);
 const lx=Math.cos((s.sun-.25)*Math.PI*2)*18,ly=Math.max(2,Math.sin((s.sun-.25)*Math.PI*2)*16),lz=7,ll=Math.hypot(lx,ly,lz);
 for(const {vs,polygon}of frame.triangles){const [a,b,c]=vs,ux=b.x-a.x,uy=b.y-a.y,uz=b.z-a.z,vx=c.x-a.x,vy=c.y-a.y,vz=c.z-a.z,nx=uy*vz-uz*vy,ny=uz*vx-ux*vz,nz=ux*vy-uy*vx,l=Math.hypot(nx,ny,nz)||1,light=(nx*lx+ny*ly+nz*lz)/l/ll;
  const color=world.terrainColor(a.x,a.y,a.z,light,ny/l);layer.beginPath();appendPath(layer,polygon);layer.fillStyle=color;layer.fill();layer.strokeStyle=color;layer.lineWidth=.55;layer.stroke();
 }
 ctx.drawImage(canvas,0,0,world.w,world.h);
}

export function disposeCanvasCloseSurface(world){
 frames.delete(world);
 for(const key of['closeTerrainCanvas','closeLakeCanvas']){if(world[key])world[key].width=world[key].height=1;delete world[key];}
 for(const key of['closeSurfaceFrame','closeTerrainFrame','closeLakeFrame','closeTerrainKey','closeLakeKey'])delete world[key];
}
